import { AlertCircle } from 'lucide-react';
import { useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/**
 * /auth/error?reason=… — единственный экран, на который бэкенд уводит человека,
 * когда ссылка из письма больше не работает.
 *
 * Маршрута не существовало, хотя редиректов на него в бэкенде пять
 * (`invitations.py` ×3, `auth.py` ×2 на 21.08). Прод 21.08.2026: человек дважды перешёл
 * по сгоревшему приглашению, оба раза получил 200 на `/auth/error`, попал в
 * `NotFoundFallback`, тот завёрнут в AppShell, AppShell дёрнул `/auth/me` → 401
 * → `/login`. Итог: немой экран входа без единого слова о том, что произошло.
 * В логах nginx это видно построчно.
 *
 * Экран standalone (без AppShell) — сюда попадают неавторизованные по
 * определению, и любой заход в shell вернёт ту же петлю на `/login`.
 */

export type AuthErrorReason =
  | 'invitation_not_found'
  | 'invitation_used'
  | 'invitation_expired'
  | 'invalid_or_expired'
  | 'no_tenant'
  | 'membership_blocked'
  | 'invitation_role_conflict';

interface AuthErrorContent {
  title: string;
  description: string;
  /** Текст кнопки. Ведёт на /login: другого действия у гостя всё равно нет. */
  action: string;
}

/**
 * Текст под причину. Разговор с человеком, а не код ошибки: он не должен
 * догадываться, что `invitation_used` — это «вы уже переходили по этой ссылке».
 *
 * Формулировки намеренно заканчиваются тем, ЧТО делать дальше. «Приглашение
 * недействительно» без следующего шага — это тот же тупик, только вежливый.
 */
export function authErrorContent(reason: string | undefined): AuthErrorContent {
  switch (reason) {
    case 'invitation_used':
      return {
        title: 'Приглашение уже использовано',
        description:
          'По этой ссылке вход уже выполняли — второй раз она не работает. Учётная запись создана: войдите по паролю или закажите вход по ссылке на почту.',
        action: 'Перейти ко входу',
      };
    case 'invitation_expired':
      return {
        title: 'Срок приглашения истёк',
        description:
          'Ссылка была действительна ограниченное время. Попросите пригласившего отправить приглашение заново — или войдите, если учётная запись уже создана.',
        action: 'Перейти ко входу',
      };
    case 'invitation_not_found':
      return {
        title: 'Приглашение не найдено',
        description:
          'Ссылка неполная или была отозвана. Так бывает, если письмо переслали и адрес обрезался. Попросите пригласившего отправить приглашение заново.',
        action: 'Перейти ко входу',
      };
    case 'invalid_or_expired':
      return {
        title: 'Ссылка для входа больше не действует',
        description:
          'Ссылки на почту живут 15 минут и срабатывают один раз. Закажите новую — она придёт на тот же адрес.',
        action: 'Заказать новую ссылку',
      };
    case 'no_tenant':
      return {
        title: 'Нет доступа ни к одному кабинету',
        description:
          'Учётная запись есть, но её пока не добавили ни в одну компанию. Обратитесь к тому, кто вас приглашал, — доступ выдаёт он.',
        action: 'Перейти ко входу',
      };
    // AK-917: человек заблокирован в той области, куда его пригласили. Новое
    // приглашение блок не снимает — снимает только администратор компании.
    case 'membership_blocked':
      return {
        title: 'Участник заблокирован',
        description:
          'Доступ к этой компании для вас приостановлен, поэтому приглашение не сработало. Чтобы вернуть доступ, обратитесь к администратору компании.',
        action: 'Перейти ко входу',
      };
    // ADR-081 D10(в): у человека в компании одна роль на все хозяйства.
    // Приглашение с другой ролью откатывается целиком — прежний доступ цел.
    case 'invitation_role_conflict':
      return {
        title: 'У вас в этой компании уже другая роль',
        description:
          'В приглашении указана другая роль, а в одной компании у человека одна роль на все хозяйства. Ничего не изменилось: войти можно с прежней ролью. Если роль нужно сменить или добавить хозяйство, обратитесь к администратору компании.',
        action: 'Перейти ко входу',
      };
    default:
      return {
        title: 'Ссылка не сработала',
        description:
          'Не удалось выполнить вход по этой ссылке. Попробуйте войти обычным способом или закажите вход по ссылке на почту.',
        action: 'Перейти ко входу',
      };
  }
}

export function AuthErrorPage() {
  const search = useSearch({ from: '/auth/error' }) as { reason?: string };
  const { title, description, action } = authErrorContent(search.reason);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <AlertCircle
            className="h-8 w-8 mx-auto text-muted-foreground"
            aria-hidden
          />
          <CardTitle className="text-xl font-semibold">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Полная навигация, а не router.navigate: страница входа должна
              подняться с чистого листа — сюда приходят с мёртвой сессией. */}
          <Button
            className="w-full"
            size="lg"
            onClick={() => window.location.replace('/login')}
          >
            {action}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default AuthErrorPage;
